import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
import {
  submitEmailToHubSpot,
  isValidEmail,
  trackEmailGateEvent,
} from '../services/EmailGatingService';

interface EmailGateModalProps {
  isOpen: boolean;
  onSubmit: (email: string) => void;
  onSkip?: () => void;
  role: 'tech' | 'exec' | null;
  industry: string | null;
  mode?: 'pre-exercise' | 'post-exercise';
}

export const EmailGateModal: React.FC<EmailGateModalProps> = ({
  isOpen,
  onSubmit,
  onSkip,
  role,
  industry,
  mode = 'pre-exercise',
}) => {
  const { isDark } = useTheme();
  const isPostExercise = mode === 'post-exercise';
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      trackEmailGateEvent('email_gate_view', { role, industry });
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, role, industry]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!isValidEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await submitEmailToHubSpot({ email, role, industry });

      if (result.success) {
        trackEmailGateEvent('email_gate_submit', { role, industry });
        onSubmit(email);
      } else {
        // Even if HubSpot fails, let the user proceed but log the error
        console.warn('HubSpot submission failed:', result.error);
        trackEmailGateEvent('email_gate_submit', {
          role,
          industry,
          hubspot_error: result.error,
        });
        onSubmit(email);
      }
    } catch (err) {
      // Network error, let user proceed anyway
      console.warn('Email submission error:', err);
      onSubmit(email);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3, type: 'spring', damping: 25 }}
            className="fixed inset-0 flex items-center justify-center z-50 p-4"
          >
            <div
              className={`max-w-md w-full rounded-2xl shadow-2xl ${
                isDark
                  ? 'bg-gradient-to-br from-dark-secondary to-dark-accent border border-aurora-violet-500/30'
                  : 'bg-white border border-slate-200'
              }`}
            >
              {/* Content */}
              <div className="p-8">
                {/* Icon */}
                <div className="flex justify-center mb-6">
                  <div
                    className={`w-16 h-16 rounded-full flex items-center justify-center ${
                      isDark
                        ? 'bg-aurora-violet-500/20'
                        : 'bg-professional-blue-100'
                    }`}
                  >
                    <svg
                      className={`w-8 h-8 ${
                        isDark
                          ? 'text-aurora-violet-400'
                          : 'text-professional-blue-600'
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                </div>

                {/* Title */}
                <h2
                  className={`text-2xl font-semibold text-center mb-3 ${
                    isDark ? 'text-white' : 'text-slate-900'
                  }`}
                >
                  {isPostExercise ? 'Want the facilitator guide?' : 'Before You Begin'}
                </h2>

                {/* Description */}
                <p
                  className={`text-center mb-6 ${
                    isDark ? 'text-slate-300' : 'text-slate-600'
                  }`}
                >
                  {isPostExercise
                    ? 'Get a facilitator guide and executive debrief template to run this with your team.'
                    : 'Enter your email to receive your results and access the full simulation.'}
                </p>

                {/* Form */}
                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <label
                      htmlFor="email"
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? 'text-slate-300' : 'text-slate-700'
                      }`}
                    >
                      Work Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setError(null);
                      }}
                      placeholder="you@company.com"
                      className={`w-full px-4 py-3 rounded-lg border transition-all duration-200 ${
                        isDark
                          ? 'bg-dark-primary border-aurora-violet-500/30 text-white placeholder:text-slate-500 focus:border-aurora-violet-500 focus:ring-2 focus:ring-aurora-violet-500/20'
                          : 'bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-professional-blue-500 focus:ring-2 focus:ring-professional-blue-500/20'
                      } ${error ? 'border-red-500' : ''}`}
                      disabled={isSubmitting}
                      autoFocus
                    />
                    {error && (
                      <p className="mt-2 text-sm text-red-500">{error}</p>
                    )}
                  </div>

                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 ${
                      isDark
                        ? 'bg-gradient-to-r from-aurora-violet-600 to-aurora-violet-700 text-white hover:shadow-aurora-violet disabled:opacity-50'
                        : 'bg-gradient-to-r from-professional-blue-600 to-professional-blue-700 text-white hover:shadow-lg disabled:opacity-50'
                    }`}
                    whileHover={!isSubmitting ? { scale: 1.02 } : {}}
                    whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg
                          className="animate-spin h-5 w-5"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Submitting...
                      </span>
                    ) : (
                      isPostExercise ? 'Send Me the Guide' : 'Begin Simulation'
                    )}
                  </motion.button>

                  {/* Skip button for post-exercise mode */}
                  {isPostExercise && onSkip && (
                    <button
                      type="button"
                      onClick={() => {
                        trackEmailGateEvent('email_gate_skip', { role, industry, mode });
                        onSkip();
                      }}
                      className={`w-full mt-3 py-3 px-6 rounded-lg font-medium transition-all duration-200 ${
                        isDark
                          ? 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
                          : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      No thanks
                    </button>
                  )}
                </form>

                {/* Privacy note */}
                <p
                  className={`mt-6 text-xs text-center ${
                    isDark ? 'text-slate-500' : 'text-slate-400'
                  }`}
                >
                  We respect your privacy. View our{' '}
                  <a
                    href="https://iomergent.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`underline hover:no-underline ${
                      isDark
                        ? 'text-aurora-violet-400'
                        : 'text-professional-blue-600'
                    }`}
                  >
                    Privacy Policy
                  </a>
                  .
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default EmailGateModal;
